import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '../../../components/ui/sheet.tsx';
import { Badge } from '../../../components/ui/badge.tsx';
import { type FlagDefinition, type FlagRule, RuleType } from '../api/types.ts';

interface FlagDetailSheetProps {
  flag: FlagDefinition | null;
  environment: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function FlagDetailSheet({ flag, environment, open, onOpenChange }: FlagDetailSheetProps) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side='right' className='w-full sm:max-w-lg overflow-y-auto'>
        {flag && (
          <>
            <SheetHeader>
              <div className='flex items-center gap-2'>
                <SheetTitle className='font-mono text-base'>{flag.key}</SheetTitle>
                {flag.enabled
                  ? <Badge className='bg-green-600 hover:bg-green-600'>Enabled</Badge>
                  : <Badge variant='secondary'>Disabled</Badge>}
              </div>
              <SheetDescription>{flag.description || 'No description'}</SheetDescription>
              <div className='text-xs text-muted-foreground'>
                Environment: <span className='font-mono'>{environment}</span>
              </div>
            </SheetHeader>

            <section className='mt-6 space-y-2'>
              <h3 className='text-sm font-semibold'>Rules ({flag.rules.length})</h3>
              {flag.rules.length === 0
                ? <p className='text-sm text-muted-foreground'>No rules configured.</p>
                : (
                  <ul className='space-y-2'>
                    {[...flag.rules]
                      .sort((a, b) => a.priority - b.priority)
                      .map((rule, i) => <RuleRow key={i} rule={rule} />)}
                  </ul>
                )}
            </section>

            {flag.variants && Object.keys(flag.variants).length > 0 && (
              <section className='mt-6 space-y-2'>
                <h3 className='text-sm font-semibold'>Variants</h3>
                <pre className='text-xs bg-muted p-2 rounded overflow-x-auto'>
                  {JSON.stringify(flag.variants, null, 2)}
                </pre>
              </section>
            )}
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

const RULE_TYPE_LABELS: Record<RuleType, string> = {
  [RuleType.USER]: 'user',
  [RuleType.CUSTOMER]: 'customer',
  [RuleType.PERCENTAGE_USERS]: 'percentage (users)',
  [RuleType.PERCENTAGE_CUSTOMERS]: 'percentage (customers)',
  [RuleType.ATTRIBUTE]: 'attribute',
  [RuleType.DEFAULT]: 'default',
};

function formatRuleSummary(rule: FlagRule): string {
  switch (rule.type) {
    case RuleType.USER:
    case RuleType.CUSTOMER:
      return rule.targetId ?? '(no target)';
    case RuleType.PERCENTAGE_USERS:
    case RuleType.PERCENTAGE_CUSTOMERS:
      return rule.percentage != null ? `${rule.percentage}%` : '(no percentage)';
    case RuleType.ATTRIBUTE:
      return rule.attributes && Object.keys(rule.attributes).length > 0
        ? Object.entries(rule.attributes)
          .map(([k, v]) => `${k}=${typeof v === 'string' ? v : JSON.stringify(v)}`)
          .join(', ')
        : '(no attributes)';
    case RuleType.DEFAULT:
      return 'fallback';
  }
}

function RuleRow({ rule }: { rule: FlagRule }) {
  const isDefault = rule.type === RuleType.DEFAULT;
  return (
    <li
      className={`border rounded p-2 flex items-center gap-2 text-sm ${
        isDefault ? 'border-dashed bg-muted/30' : ''
      } ${rule.enabled ? '' : 'opacity-60'}`}
    >
      <span
        className='font-mono text-xs text-muted-foreground shrink-0'
        title='Priority'
      >
        P{rule.priority}
      </span>
      <span className='text-xs uppercase tracking-wide text-muted-foreground shrink-0'>
        {RULE_TYPE_LABELS[rule.type]}
      </span>
      <span className='font-mono text-xs truncate flex-1' title={formatRuleSummary(rule)}>
        {formatRuleSummary(rule)}
      </span>
      {rule.variant && (
        <Badge variant='outline' className='shrink-0 font-mono text-xs'>
          {rule.variant}
        </Badge>
      )}
      {rule.enabled
        ? <Badge className='shrink-0 bg-green-600 hover:bg-green-600'>on</Badge>
        : <Badge variant='secondary' className='shrink-0'>off</Badge>}
    </li>
  );
}
